import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Container,
  Heading,
  Input,
  Grid,
  Text,
  SimpleGrid,
  Select,
  Image,
  Flex,
  Table,
  Thead,
  Tr,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Button,
} from '@chakra-ui/react';
import { Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { TriangleUpIcon, TriangleDownIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { TableHeader } from './shared/TableHeader';
import { brandColors, chartColors } from '../config/styleConfig';
import BackButton from './shared/BackButton';

interface RaceStats {
  id: string;
  name: string;
  team: string;
  position: string;
  total_time: string;
  first_run: string;
  second_run: string;
  race_category: string;
  race_code: string;
  race_data?: {
    race_date: string;
    race_description: string;
  };
}

interface DashboardStats {
  totalRaces: number;
  totalRacers: number;
  totalCategories: number;
  topTeam: { name: string; count: number };
  top_teams: Array<{ team: string; count: number }>;
}

interface SortConfig {
  key: keyof RaceStats | null;
  direction: 'asc' | 'desc' | null;
}

interface TeamSummary {
  team: string;
  count: number;
}

// Define blue color pattern for Teams chart
const blueColors = [
  '#2c58dc',  // primary blue
  '#6694ec',  // light blue
  '#3423ba',  // deep blue
  '#3b3dcd',  // medium blue
  '#4b6cdc',  // blue-grey
  '#8ba3e8',  // lighter blue
  '#d4ddf5',  // very light blue
  '#edf3fa',  // lightest blue
  '#5470d6',  // another blue variation
  '#7389e8',  // another light blue
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [racers, setRacers] = useState<RaceStats[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalRaces: 0,
    totalRacers: 0,
    totalCategories: 0,
    topTeam: { name: '', count: 0 },
    top_teams: []
  });
  // Store races with date so we can sort them and display date in the quick selector
  const [races, setRaces] = useState<{ race_code: string; race_description: string; race_date: string }[]>([]);
  const [filter, setFilter] = useState({ 
    name: '', 
    team: '', 
    race_category: '',
    race_code: '' 
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ 
    key: null,
    direction: null
  });
  const [positionStats, setPositionStats] = useState<Record<string, number>>({});
  const [raceDescriptions, setRaceDescriptions] = useState<Record<string, string>>({});
  const [teamStats, setTeamStats] = useState<TeamSummary[]>([]);
  // When true, results table is shown and table data (fetchData) is loaded.
  // No session persistence: each visit starts with the button visible until user loads the table.
  const [raceTableLoaded, setRaceTableLoaded] = useState(false);

  const fetchData = React.useCallback(async () => {
    let query = supabase
      .from('medvedica_race_stats')
      .select(`
        *,
        race_data:medvedica_race_data(
          race_date,
          race_description
        )
      `);

    if (filter.name) {
      query = query.ilike('name', `%${filter.name}%`);
    }
    if (filter.team) {
      query = query.ilike('team', `%${filter.team}%`);
    }
    if (filter.race_category) {
      query = query.ilike('race_category', `%${filter.race_category}%`);
    }
    if (filter.race_code) {
      query = query.eq('race_code', filter.race_code);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching racers:', error);
      return;
    }

    setRacers(data || []);
  }, [filter]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const { data: raceStats, error: raceError } = await supabase
          .from('medvedica_race_stats')
          .select('*');

        if (raceError) throw raceError;

        // Total racers: same source as /racers page (frontend_racers_summary_mv)
        const { count: totalRacersCount, error: racersCountError } = await supabase
          .from('frontend_racers_summary_mv')
          .select('*', { count: 'exact', head: true });

        if (racersCountError) {
          // 57014 = statement timeout; view query too slow
          const msg = (racersCountError as { message?: string; code?: string }).message ?? '';
          const code = (racersCountError as { code?: string }).code;
          console.error('Error fetching racers count:', code || msg, racersCountError);
        }

        // Ensure we are working with a concrete array of race stats
        const typedRaceStats = (raceStats ?? []) as RaceStats[];

        // Calculate team stats
        const teamCounts = typedRaceStats.reduce((acc: { [key: string]: number }, stat) => {
          if (stat.team) {
            acc[stat.team] = (acc[stat.team] || 0) + 1;
          }
          return acc;
        }, {});

        // Convert to array and sort
        const sortedTeams = Object.entries(teamCounts)
          .map(([team, count]) => ({ team, count }))
          .sort((a, b) => b.count - a.count);

        const topTeam = sortedTeams[0] || { team: '', count: 0 };

        // Update both stats and teamStats (totalRacers from view to match /racers list)
        setStats({
          totalRaces: new Set(typedRaceStats.map(stat => stat.race_code)).size,
          totalRacers: totalRacersCount ?? 0,
          totalCategories: new Set(typedRaceStats.map(stat => stat.race_category)).size,
          topTeam: { name: topTeam.team, count: topTeam.count },
          top_teams: sortedTeams
        });

        // Set teamStats for the chart
        setTeamStats(sortedTeams.slice(0, 10)); // Take top 10 teams

      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    fetchDashboardStats();
  }, []);

  // Update the type for team in filter function
  const topTeams = stats.top_teams.filter((team: { team: string; count: number }) => team.count > 5);

  // Update chart data
  const chartData = {
    labels: topTeams.map((t: { team: string }) => t.team),
    datasets: [
      {
        data: topTeams.map((t: { count: number }) => t.count),
        backgroundColor: chartColors.blues,
        borderColor: 'white',
        borderWidth: 1,
      },
    ],
  };

  // Add click handler for chart segments
  const handleChartClick = (event: any, elements: any[]) => {
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      const teamName = stats.top_teams[clickedIndex].team;
      navigate(`/teamDetail/${encodeURIComponent(teamName)}`);
    }
  };

  // Update chart options to include onClick and hover styles
  const chartOptions = {
    plugins: {
      legend: {
        position: 'right' as const,
        onClick: (e: any, legendItem: any) => {
          const teamName = stats.top_teams[legendItem.index].team;
          navigate(`/teamDetail/${encodeURIComponent(teamName)}`);
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const team = stats.top_teams[context.dataIndex].team;
            const count = stats.top_teams[context.dataIndex].count;
            return `${team}: ${count} racers`;
          }
        }
      }
    },
    onClick: handleChartClick,
    elements: {
      arc: {
        hoverOffset: 10,
        cursor: 'pointer'
      }
    },
    maintainAspectRatio: false
  };

  const normalizeTime = (timeStr: string): number => {
    if (!timeStr) return 999999;
    
    // Handle MM:SS.ms format (1:00.20)
    if (timeStr.includes(':') && timeStr.split(':').length === 2) {
      const [minutes, secondsPart] = timeStr.split(':');
      return (parseInt(minutes) * 60) + parseFloat(secondsPart);
    }
    
    // Handle HH:MM:SS.ms format (0:00:34.28)
    if (timeStr.includes(':') && timeStr.split(':').length === 3) {
      const [hours, minutes, secondsPart] = timeStr.split(':');
      return (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseFloat(secondsPart);
    }
    
    // Handle SS.ms format (50.95)
    return parseFloat(timeStr);
  };

  const sortData = (data: RaceStats[]): RaceStats[] => {
    // Always apply default sorting
    return [...data].sort((a, b) => {
      // Primary sort by race date (newest first)
      const dateA = a.race_data?.race_date || '';
      const dateB = b.race_data?.race_date || '';
      const dateCompare = dateB.localeCompare(dateA); // descending (newest first)
      if (dateCompare !== 0) return dateCompare;
      
      // Secondary sort by race category (alphabetically)
      const categoryA = a.race_category || '';
      const categoryB = b.race_category || '';
      const categoryCompare = categoryA.localeCompare(categoryB);
      if (categoryCompare !== 0) return categoryCompare;
      
      // Tertiary sort by position (ascending, with special handling)
      const getPositionValue = (pos: string): number => {
        if (!pos || pos === '0') return 999; // DNF or invalid positions go last
        const numPos = parseInt(pos);
        return isNaN(numPos) ? 999 : numPos; // Text values also go last
      };

      const posA = getPositionValue(a.position);
      const posB = getPositionValue(b.position);
      return posA - posB; // ascending order (1,2,3...)
    });
  };

  const handleSort = (key: keyof RaceStats) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  // Fetch table data only after user clicks "Load Race table" (keeps initial dashboard load fast)
  useEffect(() => {
    if (raceTableLoaded) {
      fetchData();
    }
  }, [fetchData, raceTableLoaded]);

  useEffect(() => {
    const fetchRaces = async () => {
      const { data, error } = await supabase
        .from('medvedica_race_data')
        .select('race_code, race_description, race_date')
        .order('race_date', { ascending: false });

      if (error) {
        console.error('Error fetching races:', error);
        return;
      }

      // Ensure races are ordered by date (newest first) even if backend ordering changes
      const raceRows = (data || []) as { race_code: string; race_description: string; race_date: string }[];
      const sortedRaces = raceRows.sort((a, b) => {
        const dateA = a.race_date || '';
        const dateB = b.race_date || '';
        return dateB.localeCompare(dateA);
      });

      setRaces(sortedRaces);
      // Create mapping from race_data results
      const descriptions = raceRows.reduce((acc, race) => ({
        ...acc,
        [race.race_code]: race.race_description
      }), {} as Record<string, string>);
      setRaceDescriptions(descriptions);
    };

    fetchRaces();
  }, []);

  // Add this useEffect to calculate position stats whenever racers data changes
  useEffect(() => {
    // Calculate position distribution
    const positionCounts = racers.reduce((acc: Record<string, number>, racer) => {
      // Only count valid positions (1-6)
      if (racer.position && Number(racer.position) >= 1 && Number(racer.position) <= 6) {
        acc[racer.position] = (acc[racer.position] || 0) + 1;
      }
      return acc;
    }, {});

    setPositionStats(positionCounts);
  }, [racers]);

  // Update the position chart data to use actual counts
  const positionChartData = {
    labels: ['1st', '2nd', '3rd', '4th', '5th', '6th'],
    datasets: [{
      data: [1,2,3,4,5,6].map(pos => positionStats[pos] || 0),
      backgroundColor: [
        '#FFD700', // Gold
        '#C0C0C0', // Silver
        '#CD7F32', // Bronze
        '#DEB887', // Light bronze
        '#E5C39E', // Lighter bronze
        '#ECD5B6', // Lightest bronze
      ],
      borderColor: 'white',
      borderWidth: 1,
    }],
  };

  // Position chart options
  const positionChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          boxWidth: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const position = context.label;
            const count = context.raw;
            return `${position}: ${count} racers`;
          }
        }
      }
    },
    maintainAspectRatio: false,
    elements: {
      arc: {
        hoverOffset: 10,
      }
    },
  };

  // Update Teams chart data to use teamStats
  const teamsChartData = {
    labels: teamStats.map(team => team.team),
    datasets: [{
      data: teamStats.map(team => team.count),
      backgroundColor: chartColors.blues,      
      borderColor: 'white',
      borderWidth: 1,
    }],
  };

  // Teams chart options
  const teamsChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          boxWidth: 20,
        },
        onClick: (e: any, legendItem: any) => {
          const team = teamStats[legendItem.index].team;
          navigate(`/teamDetail/${encodeURIComponent(team)}`);
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const team = teamStats[context.dataIndex].team;
            const count = teamStats[context.dataIndex].count;
            return `${team}: ${count} racers`;
          }
        }
      }
    },
    maintainAspectRatio: false,
    elements: {
      arc: {
        hoverOffset: 10,
        cursor: 'pointer',
      }
    },
    onClick: (event: any, elements: any[]) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        const team = teamStats[clickedIndex].team;
        navigate(`/teamDetail/${encodeURIComponent(team)}`);
      }
    },
  };

  return (
    <Container maxW="container.xl" py={8}>
      {/* Updated Title with Logo - full-width hero like Racers page (desktop) */}
      <Box mb={8} position="relative">
        <Image
          src="/img/logo-three2.jpg"
          alt="Medvedica Logo"
          width="100%"
          height="300px"
          objectFit="cover"
          objectPosition="center"
          borderRadius="lg"
          fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={6}
          background="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))"
          borderBottomRadius="lg"
        >
          <Heading 
            color="white"
            size="xl"
            textShadow="2px 2px 4px rgba(0,0,0,0.3)"
          >
            Dashboard
          </Heading>
        </Box>
      </Box>

      {/* Replace StatGroup with SimpleGrid of clickable boxes */}
      <SimpleGrid columns={[1, 2, 4]} gap={6} mb={8}>
        <Box 
          p={5} 
          shadow="md" 
          borderWidth="1px" 
          borderRadius="md"
          cursor="pointer"
          onClick={() => navigate('/races')}
          _hover={{ 
            transform: 'translateY(-2px)',
            shadow: 'lg',
            borderColor: brandColors.accent 
          }}
          transition="all 0.2s"
        >
          <Text fontSize="lg" fontWeight="bold">Total Races</Text>
          <Text fontSize="3xl">{stats.totalRaces}</Text>
        </Box>

        <Box 
          p={5} 
          shadow="md" 
          borderWidth="1px" 
          borderRadius="md"
          cursor="pointer"
          onClick={() => navigate('/racers')}
          _hover={{ 
            transform: 'translateY(-2px)',
            shadow: 'lg',
            borderColor: brandColors.accent 
          }}
          transition="all 0.2s"
        >
          <Text fontSize="lg" fontWeight="bold">Total Racers</Text>
          <Text fontSize="3xl">{stats.totalRacers}</Text>
        </Box>

        <Box 
          p={5} 
          shadow="md" 
          borderWidth="1px" 
          borderRadius="md"
          cursor="pointer"
          onClick={() => navigate('/races')}
          _hover={{ 
            transform: 'translateY(-2px)',
            shadow: 'lg',
            borderColor: brandColors.accent 
          }}
          transition="all 0.2s"
        >
          <Text fontSize="lg" fontWeight="bold">Total Categories</Text>
          <Text fontSize="3xl">{stats.totalCategories}</Text>
        </Box>

        <Box 
          p={5} 
          shadow="md" 
          borderWidth="1px" 
          borderRadius="md"
          cursor="pointer"
          onClick={() => navigate(`/teamDetail/${encodeURIComponent(stats.topTeam.name)}`)}
          _hover={{ 
            transform: 'translateY(-2px)',
            shadow: 'lg',
            borderColor: brandColors.accent 
          }}
          transition="all 0.2s"
        >
          <Text fontSize="lg" fontWeight="bold">Top Team</Text>
          <Text fontSize="3xl">{stats.topTeam.name}</Text>
          <Text fontSize="sm" color="gray.500">{stats.topTeam.count} participations</Text>
        </Box>
      </SimpleGrid>

      {/* Charts Section */}
      <SimpleGrid columns={[1, null, 2]} gap={6} mb={8}>
        {/* Top 10 Teams Chart */}
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" h="400px">
          <Heading size="md" mb={4}>Top 10 Teams</Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Teams with most racers
          </Text>
          <Box h="300px">
            <Pie 
              data={teamsChartData}
              options={teamsChartOptions}
            />
          </Box>
        </Box>

        {/* Position Distribution Chart */}
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" h="400px">
          <Heading size="md" mb={4}>Position Distribution</Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Distribution of race positions
          </Text>
          <Box h="300px">
            <Pie 
              data={positionChartData}
              options={positionChartOptions}
            />
          </Box>
        </Box>
      </SimpleGrid>

      {/* Results Table – hidden until user clicks "Load Race table"; then data is fetched and table shown */}
      {!raceTableLoaded ? (
        <Flex justify="center" py={8}>
          <Button
            size="lg"
            colorScheme="blue"
            onClick={() => {
              // Show the full results table and fetch data; no session persistence.
              setRaceTableLoaded(true);
            }}
            _hover={{ opacity: 0.9 }}
          >
            Load all races table
          </Button>
        </Flex>
      ) : (
      <>
        {/* Filters – shown only when table is loaded; placed above the table header */}
        <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={4}>
          <Input
            placeholder="Search by name"
            value={filter.name}
            onChange={(e) => setFilter({...filter, name: e.target.value})}
          />
          <Input
            placeholder="Search by team"
            value={filter.team}
            onChange={(e) => setFilter({...filter, team: e.target.value})}
          />
          <Input
            placeholder="Search by category"
            value={filter.race_category}
            onChange={(e) => setFilter({...filter, race_category: e.target.value})}
          />
          <Select
            placeholder="All races"
            value={filter.race_code}
            onChange={(e) => setFilter({...filter, race_code: e.target.value})}
            fontSize="sm"
          >
            {races.map(race => {
              const dateLabel = race.race_date
                ? new Date(race.race_date).toLocaleDateString()
                : '';
              const label = dateLabel
                ? `${dateLabel} • ${race.race_description}`
                : race.race_description;

              return (
                <option
                  key={race.race_code}
                  value={race.race_code}
                  style={{ fontSize: '0.8rem' }}
                >
                  {label}
                </option>
              );
            })}
          </Select>
        </Grid>
        <Box overflowX="auto">
        <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
          <Thead bg="gray.50">
            <Tr>
              <TableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Team" sortKey="team" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Position" sortKey="position" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Total Time" sortKey="total_time" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Category" sortKey="race_category" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Race" sortKey="race_code" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="First Run" sortKey="first_run" sortConfig={sortConfig} onSort={handleSort} />
              <TableHeader label="Second Run" sortKey="second_run" sortConfig={sortConfig} onSort={handleSort} />
            </Tr>
          </Thead>
          <tbody>
            {sortData(racers).map((racer) => (
              <tr key={racer.id}>
                <td 
                  style={{ 
                    padding: '8px 10px', 
                    borderBottom: '1px solid #E2E8F0',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/racer/${encodeURIComponent(racer.name)}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'darkblue';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {racer.name}
                </td>
                {/* Team cell – truncated with ellipsis to prevent multi-line row inflation */}
                <td 
                  style={{ 
                    padding: '8px 10px', 
                    borderBottom: '1px solid #E2E8F0', 
                    cursor: racer.team ? 'pointer' : 'default',
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={racer.team || ''}
                  onClick={() => racer.team && navigate(`/teamDetail/${encodeURIComponent(racer.team)}`)}
                  onMouseEnter={(e) => {
                    if (racer.team) {
                      e.currentTarget.style.color = 'darkblue';
                      e.currentTarget.style.textDecoration = 'underline';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {racer.team || '-'}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
                  {racer.position || 'DNF'}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.total_time}</td>
                <td 
                  style={{ 
                    padding: '8px 10px', 
                    borderBottom: '1px solid #E2E8F0', 
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/category/${encodeURIComponent(racer.race_category)}?race_code=${racer.race_code}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'darkblue';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {racer.race_category}
                </td>
                {/* Race description – truncated to prevent multi-line overflow on mobile */}
                <td 
                  style={{ 
                    padding: '8px 10px', 
                    borderBottom: '1px solid #E2E8F0',
                    cursor: 'pointer',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={raceDescriptions[racer.race_code] || racer.race_code}
                  onClick={() => navigate(`/race/${encodeURIComponent(racer.race_code)}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'darkblue';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {raceDescriptions[racer.race_code] || racer.race_code}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.first_run}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.second_run}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
      </>
      )}

    </Container>
  );
} 